import { useState, useEffect, useMemo } from 'react';
import { Mountain, Download } from 'lucide-react';
import { VEIBarChart } from '../components/Charts/VEIBarChart';
import { fetchVolcanoVEIDistribution, fetchVolcanoRockTypes } from '../api/volcanoes';
import { RockTypeBadges } from '../components/RockTypeBadges';
import { showError, showSuccess } from '../utils/toast';
import { useKeyboardShortcuts, commonShortcuts } from '../hooks/useKeyboardShortcuts';
import { CardSkeleton, ChartSkeleton } from '../components/LoadingSkeleton';
import { EmptyState } from '../components/EmptyState';
import {
  createVolcanoAutocompleteOptions,
  filterVolcanoAutocompleteOptions,
  type VolcanoAutocompleteOption,
  type VolcanoAutocompleteSource,
} from '../utils/volcanoAutocomplete';
import type { VEIDistribution, RockType } from '../types';
import { Combobox } from '../components/Common/Combobox';
import { HelpTooltip } from '../components/Common/HelpTooltip';
import { usePlotTheme } from '../hooks/usePlotTheme';
import { VolcanoSamplesMap } from '../components/Map/VolcanoSamplesMap';

interface VolcanoVEISelection {
  name: string;
  number: number;
  data: VEIDistribution | null;
  rockTypes: RockType[] | null;
  loading: boolean;
  error: string | null;
}

// Compared volcanoes: marks use the theme series colours (hooks/usePlotTheme),
// text uses their readable variants (index.css)
const VOLCANO_TEXT_COLORS = ['rgb(var(--series-1-text))', 'rgb(var(--series-2-text))', 'rgb(var(--series-3-text))'];

const CompareVEIPage = () => {
  const { palette: { series } } = usePlotTheme();
  const [volcanoes, setVolcanoes] = useState<VolcanoAutocompleteSource[]>([]);
  const [searchInputs, setSearchInputs] = useState<string[]>(['', '']);
  
  const [selections, setSelections] = useState<VolcanoVEISelection[]>([
    { name: '', number: 0, data: null, rockTypes: null, loading: false, error: null },
    { name: '', number: 0, data: null, rockTypes: null, loading: false, error: null },
  ]);

  // Load volcano names on mount
  useEffect(() => {
    const loadVolcanoes = async () => {
      try {
        const response = await fetch('/api/volcanoes/summary');
        const data = await response.json();
        setVolcanoes(data.data || []);
      } catch (err) {
        console.error('Failed to load volcanoes:', err);
      }
    };
    loadVolcanoes();
  }, []);

  const volcanoOptions = useMemo(
    () => createVolcanoAutocompleteOptions(volcanoes),
    [volcanoes],
  );

  const handleVolcanoSelect = async (index: number, volcano: VolcanoAutocompleteOption) => {
    const newInputs = [...searchInputs];
    newInputs[index] = volcano.label;
    setSearchInputs(newInputs);

    // Update selection and set loading state
    setSelections((prev) =>
      prev.map((sel, i) =>
        i === index
          ? { name: volcano.volcano_name, number: volcano.volcano_number, data: null, rockTypes: null, loading: true, error: null }
          : sel
      )
    );

    try {
      // Fetch both VEI distribution and rock types in parallel
      const [data, rockTypesData] = await Promise.all([
        fetchVolcanoVEIDistribution(volcano.volcano_number),
        fetchVolcanoRockTypes(volcano.volcano_number)
      ]);
      
      setSelections((prev) =>
        prev.map((sel, i) =>
          i === index
            ? { ...sel, data, rockTypes: rockTypesData.rock_types, loading: false, error: null }
            : sel
        )
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch data';
      showError(`Failed to load ${volcano.volcano_name}: ${errorMessage}`);
      setSelections((prev) =>
        prev.map((sel, i) =>
          i === index
            ? { ...sel, data: null, rockTypes: null, loading: false, error: errorMessage }
            : sel
        )
      );
    }
  };

  const handleClearVolcano = (index: number) => {
    setSelections((prev) =>
      prev.map((sel, i) =>
        i === index
          ? { name: '', number: 0, data: null, rockTypes: null, loading: false, error: null }
          : sel
      )
    );
  };

  const exportToCSV = () => {
    // Helper to escape CSV fields
    const escapeCSV = (field: string) => {
      if (field.includes(',') || field.includes('"') || field.includes('\n')) {
        return `"${field.replaceAll('"', '""')}"`;
      }
      return field;
    };

    const rows: string[] = ['Volcano,VEI Level,Eruption Count'];

    for (const sel of selections) {
      if (sel.data?.vei_counts) {
        for (const [vei, count] of Object.entries(sel.data.vei_counts)) {
          rows.push(`${escapeCSV(sel.name)},${vei},${count}`);
        }
      }
    }

    const csv = rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const volcanoNames = selections
      .filter((s) => s.name)
      .map((s) => s.name.replaceAll(' ', '_'))
      .join('_vs_');
    link.download = `compare_${volcanoNames || 'volcanoes'}_VEI.csv`;

    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    
    showSuccess('VEI comparison data exported successfully!');
  };

  // Keyboard shortcut for CSV export
  useKeyboardShortcuts([
    commonShortcuts.download(exportToCSV),
  ]);

  const hasAnyData = selections.some((sel) => sel.data !== null);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-surface border-b border-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-3">
                <Mountain className="w-8 h-8 text-volcano-600" aria-hidden="true" />
                <h1 className="text-2xl font-bold text-gray-900">Compare VEI Distributions</h1>
              </div>
              <p className="mt-1 text-sm text-gray-600">
                Compare Volcanic Explosivity Index (VEI) distributions between volcanoes
              </p>
            </div>
            {hasAnyData && (
              <button
                onClick={exportToCSV}
                className="flex items-center gap-2 px-4 py-2 bg-volcano-600 text-white rounded-lg hover:bg-volcano-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-volcano-500 focus:ring-offset-2"
                aria-label="Download Combined CSV of the VEI comparison"
                title="Download CSV (Ctrl+D / Cmd+D)"
              >
                <Download className="w-4 h-4" aria-hidden="true" />
                Download Combined CSV
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* About VEI Info Box */}
        <div className="mb-6 note">
          <p className="font-semibold text-gray-900 mb-1">About VEI</p>
          <p>
            The Volcanic Explosivity Index (VEI) is a logarithmic scale (0-8) measuring the
            explosiveness of volcanic eruptions based on ejecta volume and eruption column height.
          </p>
        </div>

      {/* Volcano Comparison Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {selections.map((selection, index) => (
          <div
            key={selection.number || `empty-${index}`}
            className="card p-6"
          >
            {/* Volcano Selector */}
            <div className="mb-6">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                <span className="series-swatch" style={{ backgroundColor: series[index] }} aria-hidden="true" />
                Volcano {index + 1}
              </h2>
              <div className="relative">
                <Combobox
                  value={searchInputs[index]}
                  onValueChange={(value) =>
                    setSearchInputs((prev) => prev.map((input, i) => (i === index ? value : input)))
                  }
                  options={
                    searchInputs[index]
                      ? filterVolcanoAutocompleteOptions(volcanoOptions, searchInputs[index]).slice(0, 10)
                      : []
                  }
                  getOptionKey={(volcano) => volcano.volcano_number}
                  getOptionLabel={(volcano) => volcano.label}
                  onSelect={(volcano) => handleVolcanoSelect(index, volcano)}
                  placeholder={`Select volcano ${index + 1}...`}
                  ariaLabel={`Search for volcano ${index + 1}`}
                  inputClassName="field"
                />
              </div>
              {selection.name && (
                <button
                  onClick={() => {
                    handleClearVolcano(index);
                    const newInputs = [...searchInputs];
                    newInputs[index] = '';
                    setSearchInputs(newInputs);
                  }}
                  aria-label={`Clear selection of volcano ${index + 1}`}
                  className="mt-2 text-sm text-red-600 hover:text-red-800 transition-colors duration-200"
                >
                  Clear selection
                </button>
              )}
            </div>

            {/* Loading State */}
            {selection.loading && (
              <div className="space-y-4">
                <ChartSkeleton height="350px" />
                <CardSkeleton />
              </div>
            )}

            {/* Error State */}
            {selection.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 font-semibold">Error</p>
                <p className="text-red-600 text-sm mt-1">{selection.error}</p>
              </div>
            )}

            {/* Data Display */}
            {selection.data && !selection.loading && !selection.error && (
              <div>
                {/* VEI Bar Chart */}
                <div className="mb-6 h-[350px]">
                  <VEIBarChart
                    veiCounts={selection.data.vei_counts}
                    volcanoName={selection.data.volcano_name}
                    color={series[index]}
                  />
                </div>

                {/* Location of the volcano and of its samples */}
                {selection.number > 0 && (
                  <div className="mb-6">
                    <h3 className="eyebrow mb-3">Location and Samples</h3>
                    <VolcanoSamplesMap volcanoNumber={selection.number} volcanoName={selection.name} height={260} />
                  </div>
                )}

                {/* Rock Type Badges */}
                {selection.rockTypes && (
                  <div className="mb-6">
                    <RockTypeBadges 
                      rockTypes={selection.rockTypes}
                      color={series[index]}
                    />
                  </div>
                )}

                {/* Statistics */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h3 className="font-semibold text-gray-800 mb-3">Summary Statistics</h3>

                  {/* Total Eruptions */}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Eruptions:</span>
                    <span className="font-semibold">{selection.data.total_eruptions}</span>
                  </div>

                  {/* VEI Range */}
                  <div className="flex justify-between">
                    <span className="text-gray-600">VEI Range:</span>
                    <span className="font-semibold">{getVEIRange(selection.data.vei_counts)}</span>
                  </div>

                  {/* Dominant VEI */}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Most Common VEI:</span>
                    <span className="font-semibold">{getDominantVEI(selection.data.vei_counts)}</span>
                  </div>

                  {/* Date Range */}
                  {selection.data.date_range && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date Range:</span>
                      <span className="font-semibold">
                        {formatDateRange(selection.data.date_range)}
                      </span>
                    </div>
                  )}

                  {/* Volcano Number */}
                  <div className="flex justify-between text-sm text-gray-500 pt-2 border-t border-gray-200">
                    <span>Volcano Number:</span>
                    <span>{selection.data.volcano_number}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!selection.name && !selection.loading && (
              <EmptyState
                icon={Mountain}
                title="No Volcano Selected"
                description="Select a volcano to view its VEI distribution"
              />
            )}
          </div>
        ))}
      </div>

      {/* Comparison Insights */}
      {selections[0].data && selections[1].data && (
        <section className="mt-8 card p-6" aria-labelledby="vei-insights">
          <div className="flex items-center justify-between mb-4">
            <h2 id="vei-insights" className="text-xl font-semibold text-gray-900">Comparison insights</h2>
            <details className="text-xs">
              <summary className="cursor-pointer text-volcano-700 hover:text-volcano-800 font-medium">How are these calculated?</summary>
              <div className="mt-2 p-3 card space-y-2 max-w-md">
                <div>
                  <p className="font-semibold text-gray-700">More Explosive:</p>
                  <p className="text-gray-600">Compares average VEI across all eruptions for each volcano.</p>
                  {(() => {
                    const avg0 = selections[0].data ? getAverageVEI(selections[0].data.vei_counts).toFixed(2) : '0';
                    const avg1 = selections[1].data ? getAverageVEI(selections[1].data.vei_counts).toFixed(2) : '0';
                    return (
                      <p className="text-xs text-gray-500 mt-1 font-mono">
                        {selections[0].name}: avg VEI = {avg0}<br/>
                        {selections[1].name}: avg VEI = {avg1}
                      </p>
                    );
                  })()}
                </div>
                <div>
                  <p className="font-semibold text-gray-700">More Active:</p>
                  <p className="text-gray-600">Simple count of total recorded eruptions.</p>
                  {(() => {
                    const total0 = selections[0].data?.total_eruptions || 0;
                    const total1 = selections[1].data?.total_eruptions || 0;
                    return (
                      <p className="text-xs text-gray-500 mt-1 font-mono">
                        {selections[0].name}: {total0} eruptions<br/>
                        {selections[1].name}: {total1} eruptions
                      </p>
                    );
                  })()}
                </div>
                <div>
                  <p className="font-semibold text-gray-700">Similarity Score:</p>
                  <p className="text-gray-600">Weighted comparison of VEI distributions. For each VEI level (0-8), compares the percentage of eruptions, weighted by total eruptions at that level. Result is averaged across all VEI levels.</p>
                </div>
              </div>
            </details>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* More Explosive */}
            <div className="card p-4">
              <p className="text-sm text-gray-600 mb-1">More Explosive</p>
              <p className="text-lg font-bold" style={{ color: getMoreExplosiveColor(selections) }}>
                {getMoreExplosiveVolcano(selections)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Based on avg VEI</p>
            </div>

            {/* More Active */}
            <div className="card p-4">
              <p className="text-sm text-gray-600 mb-1">More Active</p>
              <p className="text-lg font-bold" style={{ color: getMoreActiveColor(selections) }}>
                {getMoreActiveVolcano(selections)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Total eruptions</p>
            </div>

            {/* Similar VEI */}
            <div className="card p-4">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm text-gray-600">Similarity Score</p>
                <HelpTooltip
                  id="vei-similarity-help"
                  label="How the similarity score is calculated"
                  className="w-80 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg -left-36 top-6"
                >
                    <p className="font-semibold mb-1">How it's calculated:</p>
                    <p>Compares the percentage distribution of eruptions across VEI levels. For each VEI level, calculates how similar the proportions are, weighted by the total number of eruptions at that level.</p>
                    {(() => {
                      const data0 = selections[0].data;
                      const data1 = selections[1].data;
                      if (data0 && data1) {
                        // Find a common VEI level with data
                        const commonVEI = Object.keys(data0.vei_counts).find(vei => 
                          vei !== 'unknown' && data0.vei_counts[vei] > 0 && data1.vei_counts[vei] > 0
                        );
                        if (commonVEI) {
                          const count0 = data0.vei_counts[commonVEI];
                          const count1 = data1.vei_counts[commonVEI];
                          const pct0 = ((count0 / data0.total_eruptions) * 100).toFixed(1);
                          const pct1 = ((count1 / data1.total_eruptions) * 100).toFixed(1);
                          const diff = Math.abs(Number(pct0) - Number(pct1)).toFixed(1);
                          const similarity = (100 - Number(diff)).toFixed(1);
                          const veiLabel = Math.floor(Number(commonVEI));
                          return (
                            <div className="mt-2 p-2 bg-gray-800 rounded text-[10px] font-mono">
                              <p className="text-yellow-300 font-semibold mb-1">Example with your data:</p>
                              <p>VEI {veiLabel}: {selections[0].name} has {pct0}% ({count0}/{data0.total_eruptions})</p>
                              <p>VEI {veiLabel}: {selections[1].name} has {pct1}% ({count1}/{data1.total_eruptions})</p>
                              <p className="mt-1">Difference: |{pct0}% - {pct1}%| = {diff}%</p>
                              <p>Similarity: 100% - {diff}% = {similarity}%</p>
                              <p className="text-gray-400 mt-1">(Repeated for all VEI levels, then weighted average)</p>
                            </div>
                          );
                        }
                      }
                      return null;
                    })()}
                    <p className="mt-1 text-gray-300">100% = identical distributions, 0% = completely different</p>
                </HelpTooltip>
              </div>
              <p className="text-lg font-bold text-gray-900">
                {getVEISimilarity(selections)}%
              </p>
              <p className="text-xs text-gray-500 mt-1">Distribution overlap</p>
            </div>
          </div>
        </section>
      )}
      </div>
    </div>
  );
};

export default CompareVEIPage;

// Helper Functions

function getVEIRange(veiCounts: Record<string, number>): string {
  // Normalize VEI keys (handle '1.0' format from API)
  const numericVEIs = Object.keys(veiCounts)
    .filter((k) => k !== 'unknown' && veiCounts[k] > 0)
    .map((k) => Math.floor(Number(k)))  // Convert '1.0' to 1, etc.
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => a - b);

  if (numericVEIs.length === 0) return 'Unknown';
  if (numericVEIs.length === 1) return `VEI ${numericVEIs[0]}`;
  return `VEI ${numericVEIs[0]} - ${numericVEIs.at(-1)}`;
}

function getDominantVEI(veiCounts: Record<string, number>): string {
  let maxCount = 0;
  let dominantVEI = 'None';

  for (const [vei, count] of Object.entries(veiCounts)) {
    if (count > maxCount) {
      maxCount = count;
      // Normalize VEI display (convert '1.0' to '1')
      if (vei === 'unknown') {
        dominantVEI = 'Unknown';
      } else {
        const normalized = Math.floor(Number(vei));
        dominantVEI = `VEI ${normalized}`;
      }
    }
  }

  return `${dominantVEI} (${maxCount} eruptions)`;
}

function formatDateRange(dateRange?: { start?: string; end?: string }): string {
  if (!dateRange?.start || !dateRange?.end) return 'Unknown';
  
  try {
    // Parse ISO 8601 dates (e.g., "-032-12-31T00:00:00Z" or "2022-11-27T00:00:00Z")
    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    
    // Extract year, handling BCE dates (negative years)
    let startYear: number;
    
    if (dateRange.start.startsWith('-')) {
      // BCE date: extract year from string like "-032-12-31T00:00:00Z"
      const yearRegex = /^-0*(\d+)/;
      const yearMatch = yearRegex.exec(dateRange.start);
      startYear = yearMatch ? -Number(yearMatch[1]) : startDate.getFullYear();
    } else {
      startYear = startDate.getFullYear();
    }
    
    const endYear = endDate.getFullYear();
    
    // Format with BCE/CE notation if needed
    const startStr = startYear < 0 ? `${Math.abs(startYear)} BCE` : String(startYear);
    const endStr = String(endYear);
    
    return `${startStr} - ${endStr}`;
  } catch {
    return 'Unknown';
  }
}

function getAverageVEI(veiCounts: Record<string, number>): number {
  let totalVEI = 0;
  let totalCount = 0;

  for (const [vei, count] of Object.entries(veiCounts)) {
    if (vei !== 'unknown') {
      // Use floor to normalize '1.0' to 1, '2.0' to 2, etc.
      const veiValue = Math.floor(Number(vei));
      if (!Number.isNaN(veiValue)) {
        totalVEI += veiValue * count;
        totalCount += count;
      }
    }
  }

  return totalCount > 0 ? totalVEI / totalCount : 0;
}

function getMoreExplosiveVolcano(selections: VolcanoVEISelection[]): string {
  const avg0 = selections[0].data ? getAverageVEI(selections[0].data.vei_counts) : 0;
  const avg1 = selections[1].data ? getAverageVEI(selections[1].data.vei_counts) : 0;

  if (Math.abs(avg0 - avg1) < 0.1) return 'Similar';
  return avg0 > avg1 ? selections[0].name : selections[1].name;
}

function getMoreExplosiveColor(selections: VolcanoVEISelection[]): string {
  const avg0 = selections[0].data ? getAverageVEI(selections[0].data.vei_counts) : 0;
  const avg1 = selections[1].data ? getAverageVEI(selections[1].data.vei_counts) : 0;

  if (Math.abs(avg0 - avg1) < 0.1) return 'rgb(var(--series-neutral-text))';
  return avg0 > avg1 ? VOLCANO_TEXT_COLORS[0] : VOLCANO_TEXT_COLORS[1];
}

function getMoreActiveVolcano(selections: VolcanoVEISelection[]): string {
  const total0 = selections[0].data?.total_eruptions || 0;
  const total1 = selections[1].data?.total_eruptions || 0;

  if (total0 === total1) return 'Equal';
  return total0 > total1 ? selections[0].name : selections[1].name;
}

function getMoreActiveColor(selections: VolcanoVEISelection[]): string {
  const total0 = selections[0].data?.total_eruptions || 0;
  const total1 = selections[1].data?.total_eruptions || 0;

  if (total0 === total1) return 'rgb(var(--series-neutral-text))';
  return total0 > total1 ? VOLCANO_TEXT_COLORS[0] : VOLCANO_TEXT_COLORS[1];
}

function getVEISimilarity(selections: VolcanoVEISelection[]): number {
  if (!selections[0].data || !selections[1].data) return 0;

  const normalize = (counts: Record<string, number>): Record<string, number> => {
    const normalized: Record<string, number> = {};
    for (const [key, value] of Object.entries(counts)) {
      if (key === 'unknown') {
        normalized['unknown'] = value;
      } else {
        const veiLevel = String(Math.floor(Number(key)));
        normalized[veiLevel] = (normalized[veiLevel] || 0) + value;
      }
    }
    return normalized;
  };

  const vei0 = normalize(selections[0].data.vei_counts);
  const vei1 = normalize(selections[1].data.vei_counts);

  const allVEIs = new Set([...Object.keys(vei0), ...Object.keys(vei1)]);
  const total0 = selections[0].data.total_eruptions;
  const total1 = selections[1].data.total_eruptions;

  if (total0 === 0 || total1 === 0) return 0;

  let weightedSimilarity = 0;
  let totalWeight = 0;

  for (const vei of allVEIs) {
    const count0 = vei0[vei] || 0;
    const count1 = vei1[vei] || 0;

    const pct0 = (count0 / total0) * 100;
    const pct1 = (count1 / total1) * 100;
    const localSimilarity = 100 - Math.abs(pct0 - pct1);

    // Weight: number of eruptions in this VEI level (combined)
    const weight = count0 + count1;

    weightedSimilarity += localSimilarity * weight;
    totalWeight += weight;
  }

  return totalWeight === 0 ? 0 : Math.round(weightedSimilarity / totalWeight);
}
